/**
 * UI languages (navbar + LanguageContext).
 *
 * Upstream content APIs use short `?lang=` codes (see UPSTREAM_LANG_BY_UI).
 * Astrology / vectors APIs use Language Tags via `languageCode` (see LANGUAGE_TAG_BY_UI).
 *
 * Kannada: UI + ISO = `kn`, Language Tag = `kn_IN`, upstream `lang` = `ka`.
 * Bengali (`bn`): sent as `bn`; localized UI falls back to `en` if payload is empty.
 */
export const LANGUAGES = [
  { code: 'en', label: 'English', shortLabel: 'EN' },
  { code: 'hi', label: 'Hindi', shortLabel: 'HI' },
  { code: 'te', label: 'Telugu', shortLabel: 'TE' },
  { code: 'ta', label: 'Tamil', shortLabel: 'TA' },
  { code: 'kn', label: 'Kannada', shortLabel: 'KN' },
  { code: 'ml', label: 'Malayalam', shortLabel: 'ML' },
  { code: 'mr', label: 'Marathi', shortLabel: 'MR' },
  { code: 'gu', label: 'Gujarati', shortLabel: 'GU' },
  { code: 'bn', label: 'Bengali', shortLabel: 'BN' },
  { code: 'pa', label: 'Punjabi', shortLabel: 'PA' },
  { code: 'or', label: 'Odia', shortLabel: 'OR' },
  { code: 'as', label: 'Assamese', shortLabel: 'AS' },
  { code: 'ne', label: 'Nepali', shortLabel: 'NE' },
];

export const LANG_CODES = LANGUAGES.map((l) => l.code);

/**
 * Language Tag (astro `languageCode`) for each UI / upstream short code.
 * Matches: en_US, hi_IN, ta_IN, te_IN, kn_IN, ml_IN, mr_IN, bn_IN, gu_IN, sp, fr.
 */
export const LANGUAGE_TAG_BY_UI = {
  en: 'en_US',
  hi: 'hi_IN',
  ta: 'ta_IN',
  te: 'te_IN',
  kn: 'kn_IN',
  ml: 'ml_IN',
  mr: 'mr_IN',
  bn: 'bn_IN',
  gu: 'gu_IN',
  sp: 'sp',
  fr: 'fr',
};

/**
 * Upstream `?lang=` for content APIs. Kannada is `ka` (not ISO `kn`).
 * Navbar-only codes (pa/or/as/ne) pass through unchanged.
 */
export const UPSTREAM_LANG_BY_UI = {
  en: 'en',
  hi: 'hi',
  ta: 'ta',
  te: 'te',
  kn: 'ka',
  ml: 'ml',
  mr: 'mr',
  bn: 'bn',
  gu: 'gu',
  sp: 'sp',
  fr: 'fr',
};

/** URL / storage / Language Tag aliases → UI code. */
const UI_LANG_ALIASES = {
  ka: 'kn',
  en_us: 'en',
  hi_in: 'hi',
  ta_in: 'ta',
  te_in: 'te',
  kn_in: 'kn',
  ml_in: 'ml',
  mr_in: 'mr',
  bn_in: 'bn',
  gu_in: 'gu',
};

/** Accepted `?lang=` values (UI codes + Kannada upstream alias). */
export const API_POOJA_LANG_CODES = [...LANG_CODES, 'ka'];

function canonicalizeLangToken(lang) {
  if (lang == null || lang === '') return '';
  return String(lang).trim().toLowerCase().replace(/-/g, '_');
}

/** Resolve to navbar / LanguageContext UI code (`kn` for Kannada). */
export function normalizeUiLang(lang) {
  const raw = canonicalizeLangToken(lang) || 'en';
  const aliased = UI_LANG_ALIASES[raw] || raw;
  if (LANG_CODES.includes(aliased)) return aliased;
  if (aliased === 'sp' || aliased === 'fr') return aliased;
  return 'en';
}

/**
 * Resolve UI language to upstream `?lang=` for poojas / kshetras / gallery.
 * Kannada UI `kn` → upstream `ka`.
 */
export function normalizePoojaApiLang(lang) {
  const ui = normalizeUiLang(lang);
  if (UPSTREAM_LANG_BY_UI[ui]) return UPSTREAM_LANG_BY_UI[ui];
  return LANG_CODES.includes(ui) ? ui : 'en';
}

/** Maps app UI lang → vectors/astro `languageCode` (Language Tag). */
export function getAstroLanguageCode(lang) {
  const ui = normalizeUiLang(lang);
  return LANGUAGE_TAG_BY_UI[ui] || 'en_US';
}

/**
 * Keys to try when reading localized API maps for a UI lang.
 * Kannada may arrive as `kn` or upstream `ka`.
 */
export function localizedLangKeys(lang) {
  const ui = normalizeUiLang(lang);
  if (ui === 'kn') return ['kn', 'ka'];
  return [ui];
}

/** Order used when a translation key or API object has no string for the active `lang`. */
export const LOCALIZED_FALLBACK_ORDER = [
  'en',
  'hi',
  'te',
  'kn',
  'ka',
  'ta',
  'ml',
  'mr',
  'gu',
  'bn',
  'pa',
  'or',
  'as',
  'ne',
];

export function getLanguageLabel(code) {
  const ui = normalizeUiLang(code);
  return LANGUAGES.find((l) => l.code === ui)?.label || code;
}

export function getLanguageShortLabel(code) {
  const ui = normalizeUiLang(code);
  return LANGUAGES.find((l) => l.code === ui)?.shortLabel || String(ui || '').slice(0, 2).toUpperCase();
}

/** BCP 47 locale for `toLocaleDateString` (India / Nepal where relevant). */
export function getLocaleForLang(lang) {
  const map = {
    en: 'en-IN',
    hi: 'hi-IN',
    te: 'te-IN',
    ta: 'ta-IN',
    kn: 'kn-IN',
    ml: 'ml-IN',
    mr: 'mr-IN',
    gu: 'gu-IN',
    bn: 'bn-IN',
    pa: 'pa-IN',
    or: 'or-IN',
    as: 'as-IN',
    ne: 'ne-NP',
    sp: 'es',
    fr: 'fr',
  };
  const ui = normalizeUiLang(lang);
  return map[ui] || 'en-IN';
}
